import type { JSX } from "react";
import { lazy, Suspense, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router";

import ErrorBoundary from "~/components/ErrorBoundary";
import HelpDeskScript from "~/components/HelpDeskScript";
import LoadTriggerWrap from "~/components/LoadTriggerWrap";
import {
  drawersThatNeedEmailVerification,
  urlsThatNeedEmailVerification,
} from "~/constants/access";
import useAuth from "~/hooks/auth/useAuth";
import useClientRedirect from "~/hooks/useClientRedirect";
import useCurrentUser from "~/hooks/user/useCurrentUser";
import useUrlParams from "~/hooks/useUrlParams";

// Lazy load drawer components - only loaded when first opened
const FilterDocumentDrawer = lazy(
  () => import("~/molecules/Drawers/filters/FilterDocumentDrawer")
);
const PaymentOneTimeDrawer = lazy(
  () => import("~/molecules/Drawers/payment/PaymentOneTimeDrawer")
);

const LayoutPrivate = (): JSX.Element | null => {
  const urlToRedirectFrom = useRef<string | null>(null);
  const paramForRedirect = useRef<string | null>(null);
  const previousPathname = useRef<string | null>(null);

  const pathname = useLocation().pathname;
  const clientRedirect = useClientRedirect();
  const { params } = useUrlParams();

  const { isInitialLoad, isAuthenticated } = useAuth();
  const { isLoading, data: userData } = useCurrentUser();

  const paramsString = JSON.stringify(params);

  useEffect(() => {
    if (params && !isLoading && userData) {
      const userEmailVerified = userData.verified;

      if (!userEmailVerified) {
        // Track previous path for smart redirect logic
        const isComingFromEmailNotVerified =
          previousPathname.current === "/email-not-verified";

        // Early exit: If user is already on email-not-verified page, don't redirect again
        // This prevents the infinite redirect loop when users try to navigate away
        if (pathname === "/email-not-verified") {
          previousPathname.current = pathname;
          return;
        }

        // Smart redirect logic: Don't redirect if user is navigating away from email-not-verified
        // to a page that doesn't require verification (allow them to leave)
        if (
          isComingFromEmailNotVerified &&
          !urlsThatNeedEmailVerification.includes(pathname)
        ) {
          let hasRestrictedDrawer = false;

          for (let i = 0; i < drawersThatNeedEmailVerification.length; i++) {
            if (paramsString.includes(drawersThatNeedEmailVerification[i])) {
              hasRestrictedDrawer = true;
              break;
            }
          }

          // If no restricted drawer params, allow navigation away from email verification
          if (!hasRestrictedDrawer) {
            previousPathname.current = pathname;
            return;
          }
        }

        if (urlsThatNeedEmailVerification.includes(pathname)) {
          if (!urlToRedirectFrom.current) {
            urlToRedirectFrom.current = pathname;
            paramForRedirect.current = paramsString;
          }

          clientRedirect(
            `/email-not-verified?onVerifiedRedirect=${urlToRedirectFrom.current}&redirectParam=${paramForRedirect.current}`
          );

          return;
        }

        for (let i = 0; i < drawersThatNeedEmailVerification.length; i++) {
          if (paramsString.includes(drawersThatNeedEmailVerification[i])) {
            if (!urlToRedirectFrom.current) {
              urlToRedirectFrom.current = pathname;
              paramForRedirect.current = paramsString;
            }

            clientRedirect(
              `/email-not-verified?onVerifiedRedirect=${urlToRedirectFrom.current}&redirectParam=${paramForRedirect.current}`
            );

            return;
          }
        }
      }
    }

    // Update previous pathname for next render
    previousPathname.current = pathname;
  }, [params, paramsString, isLoading, userData, pathname, clientRedirect]);

  if (isInitialLoad && isLoading) {
    return null;
  }

  if (!isAuthenticated && !isLoading) {
    clientRedirect("/login");
    return null;
  }

  return (
    <>
      <ErrorBoundary boundaryName="Private Area" fallbackMode="detailed">
        <Outlet />
      </ErrorBoundary>
      <HelpDeskScript />
      <Suspense>
        <LoadTriggerWrap />
        <ErrorBoundary boundaryName="Payment Drawers" fallbackMode="minimal">
          <FilterDocumentDrawer />
          <PaymentOneTimeDrawer />
        </ErrorBoundary>
      </Suspense>
    </>
  );
};

export default LayoutPrivate;
